import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import {
    createFlowAngleNoise,
    type FlowAngleNoiseMode,
} from "../../utils/flowAngleNoise";
import {
    buildFlowGrid,
    traceAllCurves,
    pointsToPathD,
    generateStartPointsGrid,
    generateStartPointsRandom,
    seededRNG,
    type FlowFieldModeOptions,
    type FlowShapeMode,
    type Point2,
} from "../../utils/flowField";
import { computeFlowFieldDebugSegments } from "../../utils/flowFieldDebugSegments";
import {
    MAX_FLOW_FIELD_DOTS,
    buildFlowFieldDotSamples,
    getEffectiveDotMinGap,
} from "../../utils/flowFieldDots";

export type FlowStartMode = "grid" | "random";

interface FlowFieldState {
    width: number;
    height: number;
    marginFactor: number;
    cellSize: number;
    seed: string;
    angleNoiseMode: FlowAngleNoiseMode;
    angleNoiseOctaves: number;
    angleWarpStrength: number;
    angleWarpFrequency: number;
    noiseScale: number;
    stepNoiseScale: number;
    stepVariation: number;
    angleQuantizeDeg: number;
    fieldRotationDeg: number;
    mirrorNoiseX: boolean;
    mirrorNoiseY: boolean;
    uniformFlowBlend: number;
    uniformFlowAngleDeg: number;
    flowShapeMode: FlowShapeMode;
    /** 0.02-1: fraction of inscribed circle radius (artboard-centered). */
    flowShapeCircleRadiusScale: number;
    bidirectional: boolean;
    stepLength: number;
    maxSteps: number;
    startMode: FlowStartMode;
    startSpacing: number;
    curveCount: number;
    strokeColor: string;
    strokeWidth: number;
    backgroundColor: string;
    exportBackground: boolean;
    showAngleDebug: boolean;
    /** Render each integration step as a dot instead of a continuous stroke (Tyler Hobbs style). */
    drawDots: boolean;
    /** Extra minimum center-to-center distance between dots (SVG units); combined with stroke width when `dotMinGapAtLeastStroke`. */
    dotMinGapExtra: number;
    /** When true, effective min gap is at least the stroke width (reduces overlap when dots are large). */
    dotMinGapAtLeastStroke: boolean;
    setWidth: (v: number) => void;
    setHeight: (v: number) => void;
    setMarginFactor: (v: number) => void;
    setCellSize: (v: number) => void;
    setSeed: (v: string) => void;
    setAngleNoiseMode: (v: FlowAngleNoiseMode) => void;
    setAngleNoiseOctaves: (v: number) => void;
    setAngleWarpStrength: (v: number) => void;
    setAngleWarpFrequency: (v: number) => void;
    setNoiseScale: (v: number) => void;
    setStepNoiseScale: (v: number) => void;
    setStepVariation: (v: number) => void;
    setAngleQuantizeDeg: (v: number) => void;
    setFieldRotationDeg: (v: number) => void;
    setMirrorNoiseX: (v: boolean) => void;
    setMirrorNoiseY: (v: boolean) => void;
    setUniformFlowBlend: (v: number) => void;
    setUniformFlowAngleDeg: (v: number) => void;
    setFlowShapeMode: (v: FlowShapeMode) => void;
    setFlowShapeCircleRadiusScale: (v: number) => void;
    setBidirectional: (v: boolean) => void;
    setStepLength: (v: number) => void;
    setMaxSteps: (v: number) => void;
    setStartMode: (v: FlowStartMode) => void;
    setStartSpacing: (v: number) => void;
    setCurveCount: (v: number) => void;
    setStrokeColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    setBackgroundColor: (v: string) => void;
    setExportBackground: (v: boolean) => void;
    setShowAngleDebug: (v: boolean) => void;
    setDrawDots: (v: boolean) => void;
    setDotMinGapExtra: (v: number) => void;
    setDotMinGapAtLeastStroke: (v: boolean) => void;
    viewBoxAttr: string;
    pathDs: string[];
    /** Traced polylines (same curves as pathDs); used for dot rendering. */
    curvePoints: Point2[][];
    debugSegments: { x1: number; y1: number; x2: number; y2: number }[];
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    randomizeParameters: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const FlowFieldContext = createContext<FlowFieldState | null>(null);

const DEFAULT_FLOW_FIELD_PRESET: Record<string, unknown> = {
    width: 800,
    height: 600,
    marginFactor: 0.5,
    cellSize: 10,
    seed: "flow-1",
    angleNoiseMode: "perlin",
    angleNoiseOctaves: 4,
    angleWarpStrength: 0.35,
    angleWarpFrequency: 1,
    noiseScale: 0.08,
    stepNoiseScale: 0.09,
    stepVariation: 0,
    angleQuantizeDeg: 0,
    fieldRotationDeg: 0,
    mirrorNoiseX: false,
    mirrorNoiseY: false,
    uniformFlowBlend: 0,
    uniformFlowAngleDeg: 0,
    flowShapeMode: "full",
    flowShapeCircleRadiusScale: 1,
    bidirectional: false,
    stepLength: 2.5,
    maxSteps: 320,
    startMode: "grid",
    startSpacing: 14,
    curveCount: 900,
    strokeColor: "#2a2a2a",
    strokeWidth: 0.35,
    backgroundColor: "#ffffff",
    exportBackground: false,
    drawDots: false,
    dotMinGapExtra: 0,
    dotMinGapAtLeastStroke: true,
};

export function useFlowField() {
    const ctx = useContext(FlowFieldContext);
    if (!ctx)
        throw new Error("useFlowField must be used within FlowFieldProvider");
    return ctx;
}

function randF(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function randI(min: number, max: number): number {
    return Math.floor(randF(min, max + 0.999));
}

function randomHexByte(lo: number, hi: number): string {
    return Math.floor(randF(lo, hi))
        .toString(16)
        .padStart(2, "0");
}

export function FlowFieldProvider({ children }: { children: React.ReactNode }) {
    const [width, setWidth] = useState(800);
    const [height, setHeight] = useState(600);
    const [marginFactor, setMarginFactor] = useState(0.5);
    const [cellSize, setCellSize] = useState(10);
    const [seed, setSeed] = useState("flow-1");
    const [angleNoiseMode, setAngleNoiseMode] =
        useState<FlowAngleNoiseMode>("perlin");
    const [angleNoiseOctaves, setAngleNoiseOctaves] = useState(4);
    const [angleWarpStrength, setAngleWarpStrength] = useState(0.35);
    const [angleWarpFrequency, setAngleWarpFrequency] = useState(1);
    const [noiseScale, setNoiseScale] = useState(0.08);
    const [stepNoiseScale, setStepNoiseScale] = useState(0.09);
    const [stepVariation, setStepVariation] = useState(0);
    const [angleQuantizeDeg, setAngleQuantizeDeg] = useState(0);
    const [fieldRotationDeg, setFieldRotationDeg] = useState(0);
    const [mirrorNoiseX, setMirrorNoiseX] = useState(false);
    const [mirrorNoiseY, setMirrorNoiseY] = useState(false);
    const [uniformFlowBlend, setUniformFlowBlend] = useState(0);
    const [uniformFlowAngleDeg, setUniformFlowAngleDeg] = useState(0);
    const [flowShapeMode, setFlowShapeMode] = useState<FlowShapeMode>("full");
    const [flowShapeCircleRadiusScale, setFlowShapeCircleRadiusScale] =
        useState(1);
    const [bidirectional, setBidirectional] = useState(false);
    const [stepLength, setStepLength] = useState(2.5);
    const [maxSteps, setMaxSteps] = useState(320);
    const [startMode, setStartMode] = useState<FlowStartMode>("grid");
    const [startSpacing, setStartSpacing] = useState(14);
    const [curveCount, setCurveCount] = useState(900);
    const [strokeColor, setStrokeColor] = useState("#2a2a2a");
    const [strokeWidth, setStrokeWidth] = useState(0.35);
    const [backgroundColor, setBackgroundColor] = useState("#ffffff");
    const [exportBackground, setExportBackground] = useState(false);
    const [showAngleDebug, setShowAngleDebug] = useState(false);
    const [drawDots, setDrawDots] = useState(false);
    const [dotMinGapExtra, setDotMinGapExtra] = useState(0);
    const [dotMinGapAtLeastStroke, setDotMinGapAtLeastStroke] =
        useState(true);

    const viewBoxAttr = `0 0 ${width} ${height}`;

    const quantizeRad = useMemo(
        () => (angleQuantizeDeg > 0 ? (angleQuantizeDeg * Math.PI) / 180 : 0),
        [angleQuantizeDeg],
    );

    const flowFieldMode = useMemo(
        (): FlowFieldModeOptions => ({
            fieldRotationDeg,
            mirrorNoiseX,
            mirrorNoiseY,
            uniformFlowBlend,
            uniformFlowAngleDeg,
            flowShapeMode,
            flowShapeCircleRadiusScale,
        }),
        [
            fieldRotationDeg,
            mirrorNoiseX,
            mirrorNoiseY,
            uniformFlowBlend,
            uniformFlowAngleDeg,
            flowShapeMode,
            flowShapeCircleRadiusScale,
        ],
    );

    const flowShapeStart = useMemo(
        () =>
            flowShapeMode === "circle"
                ? ({
                      mode: "circle" as const,
                      radiusScale: flowShapeCircleRadiusScale,
                  })
                : ({ mode: "full" as const }),
        [flowShapeMode, flowShapeCircleRadiusScale],
    );

    const angleNoiseParams = useMemo(
        () => ({
            mode: angleNoiseMode,
            octaves: angleNoiseOctaves,
            warpStrength: angleWarpStrength,
            warpFrequency: angleWarpFrequency,
        }),
        [
            angleNoiseMode,
            angleNoiseOctaves,
            angleWarpStrength,
            angleWarpFrequency,
        ],
    );

    const { pathDs, curvePoints, debugSegments } = useMemo(() => {
        const noiseAngle = createFlowAngleNoise(seed || "default", angleNoiseParams);
        const noiseStep =
            stepVariation > 0
                ? createFlowAngleNoise(`${seed || "default"}|step`, {
                      mode: "perlin",
                      octaves: 1,
                      warpStrength: 0,
                      warpFrequency: 1,
                  })
                : null;
        const grid = buildFlowGrid(
            width,
            height,
            marginFactor,
            cellSize,
            noiseScale,
            noiseAngle,
            noiseStep,
            stepNoiseScale,
            flowFieldMode,
        );
        let starts: Point2[];
        if (startMode === "grid") {
            starts = generateStartPointsGrid(
                width,
                height,
                startSpacing,
                flowShapeStart,
            );
        } else {
            const rng = seededRNG(`${seed}|starts`);
            starts = generateStartPointsRandom(
                width,
                height,
                curveCount,
                rng,
                flowShapeStart,
            );
        }
        const curves = traceAllCurves(grid, starts, stepLength, maxSteps, {
            bidirectional,
            trace: {
                quantizeAngleRad: quantizeRad,
                stepVariation,
            },
        });
        const ds = curves
            .map((pts) => pointsToPathD(pts))
            .filter((d) => d.length > 0);

        const curvePoints = curves.filter((pts) => pts.length > 0);

        const debug = showAngleDebug
            ? computeFlowFieldDebugSegments(
                  width,
                  height,
                  marginFactor,
                  cellSize,
                  noiseScale,
                  noiseAngle,
                  noiseStep,
                  stepNoiseScale,
                  quantizeRad,
                  { mode: flowFieldMode, grid },
              )
            : [];

        return { pathDs: ds, curvePoints, debugSegments: debug };
    }, [
        width,
        height,
        marginFactor,
        cellSize,
        seed,
        angleNoiseParams,
        noiseScale,
        stepNoiseScale,
        stepVariation,
        quantizeRad,
        bidirectional,
        stepLength,
        maxSteps,
        startMode,
        startSpacing,
        curveCount,
        showAngleDebug,
        flowFieldMode,
        flowShapeStart,
    ]);

    const canExport = pathDs.length > 0;

    const buildExportSvgString = useCallback((): string => {
        const dotR = Math.max(0.08, strokeWidth / 2);
        const minGap = getEffectiveDotMinGap(
            strokeWidth,
            dotMinGapExtra,
            dotMinGapAtLeastStroke,
        );
        const pathsOrDots = drawDots
            ? buildFlowFieldDotSamples(
                  curvePoints,
                  MAX_FLOW_FIELD_DOTS,
                  minGap,
              ).samples
                  .map(
                      (s) =>
                          `  <circle cx="${s.x}" cy="${s.y}" r="${dotR}" fill="${strokeColor}" />`,
                  )
                  .join("\n")
            : pathDs
                  .map(
                      (d) =>
                          `  <path d="${d}" fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-linecap="round" />`,
                  )
                  .join("\n");
        const debugLines =
            showAngleDebug && debugSegments.length > 0
                ? debugSegments
                      .map(
                          (s) =>
                              `  <line x1="${s.x1}" y1="${s.y1}" x2="${s.x2}" y2="${s.y2}" stroke="#c00" stroke-width="0.2" opacity="0.5" />`,
                      )
                      .join("\n")
                : "";
        const bgRect = exportBackground
            ? `  <rect width="${width}" height="${height}" fill="${backgroundColor}" />\n`
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
${bgRect}  <g class="flow-curves">
${pathsOrDots}
  </g>
${debugLines ? `  <g class="flow-debug">\n${debugLines}\n  </g>\n` : ""}</svg>`;
    }, [
        width,
        height,
        strokeWidth,
        dotMinGapExtra,
        dotMinGapAtLeastStroke,
        drawDots,
        curvePoints,
        pathDs,
        strokeColor,
        showAngleDebug,
        debugSegments,
        exportBackground,
        backgroundColor,
        viewBoxAttr,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `flow-field-${seed.replace(/[^\w.-]+/g, "_")}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString, seed]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            width, height, marginFactor, cellSize, seed, angleNoiseMode,
            angleNoiseOctaves, angleWarpStrength, angleWarpFrequency,
            noiseScale, stepNoiseScale, stepVariation, angleQuantizeDeg,
            fieldRotationDeg,
            mirrorNoiseX, mirrorNoiseY, uniformFlowBlend, uniformFlowAngleDeg,
            flowShapeMode, flowShapeCircleRadiusScale,
            bidirectional, stepLength, maxSteps, startMode, startSpacing,
            curveCount, strokeColor, strokeWidth, backgroundColor, exportBackground,
            drawDots, dotMinGapExtra, dotMinGapAtLeastStroke,
        }),
        [
            width, height, marginFactor, cellSize, seed, angleNoiseMode,
            angleNoiseOctaves, angleWarpStrength, angleWarpFrequency,
            noiseScale, stepNoiseScale, stepVariation, angleQuantizeDeg,
            fieldRotationDeg,
            mirrorNoiseX, mirrorNoiseY, uniformFlowBlend, uniformFlowAngleDeg,
            flowShapeMode, flowShapeCircleRadiusScale,
            bidirectional, stepLength, maxSteps, startMode, startSpacing,
            curveCount, strokeColor, strokeWidth, backgroundColor, exportBackground,
            drawDots, dotMinGapExtra, dotMinGapAtLeastStroke,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.width != null) setWidth(values.width as number);
        if (values.height != null) setHeight(values.height as number);
        if (values.marginFactor != null) setMarginFactor(values.marginFactor as number);
        if (values.cellSize != null) setCellSize(values.cellSize as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (
            values.angleNoiseMode === "perlin" ||
            values.angleNoiseMode === "fbm" ||
            values.angleNoiseMode === "domain_warp"
        ) {
            setAngleNoiseMode(values.angleNoiseMode);
        }
        if (values.angleNoiseOctaves != null) {
            setAngleNoiseOctaves(values.angleNoiseOctaves as number);
        }
        if (values.angleWarpStrength != null) {
            setAngleWarpStrength(values.angleWarpStrength as number);
        }
        if (values.angleWarpFrequency != null) {
            setAngleWarpFrequency(values.angleWarpFrequency as number);
        }
        if (values.noiseScale != null) setNoiseScale(values.noiseScale as number);
        if (values.stepNoiseScale != null) setStepNoiseScale(values.stepNoiseScale as number);
        if (values.stepVariation != null) setStepVariation(values.stepVariation as number);
        if (values.angleQuantizeDeg != null) setAngleQuantizeDeg(values.angleQuantizeDeg as number);
        if (values.fieldRotationDeg != null) setFieldRotationDeg(values.fieldRotationDeg as number);
        if (values.mirrorNoiseX != null) setMirrorNoiseX(values.mirrorNoiseX as boolean);
        if (values.mirrorNoiseY != null) setMirrorNoiseY(values.mirrorNoiseY as boolean);
        if (values.uniformFlowBlend != null) setUniformFlowBlend(values.uniformFlowBlend as number);
        if (values.uniformFlowAngleDeg != null) setUniformFlowAngleDeg(values.uniformFlowAngleDeg as number);
        if (values.flowShapeMode === "full" || values.flowShapeMode === "circle") {
            setFlowShapeMode(values.flowShapeMode);
        }
        if (values.flowShapeCircleRadiusScale != null) {
            setFlowShapeCircleRadiusScale(values.flowShapeCircleRadiusScale as number);
        }
        if (values.bidirectional != null) setBidirectional(values.bidirectional as boolean);
        if (values.stepLength != null) setStepLength(values.stepLength as number);
        if (values.maxSteps != null) setMaxSteps(values.maxSteps as number);
        if (values.startMode != null) setStartMode(values.startMode as FlowStartMode);
        if (values.startSpacing != null) setStartSpacing(values.startSpacing as number);
        if (values.curveCount != null) setCurveCount(values.curveCount as number);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.strokeWidth != null) setStrokeWidth(values.strokeWidth as number);
        if (values.backgroundColor != null) setBackgroundColor(values.backgroundColor as string);
        if (values.exportBackground != null) setExportBackground(values.exportBackground as boolean);
        if (values.drawDots != null) setDrawDots(values.drawDots as boolean);
        if (values.dotMinGapExtra != null)
            setDotMinGapExtra(values.dotMinGapExtra as number);
        if (values.dotMinGapAtLeastStroke != null)
            setDotMinGapAtLeastStroke(values.dotMinGapAtLeastStroke as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "flow-field",
        defaultPreset: DEFAULT_FLOW_FIELD_PRESET,
        getPresetValues,
        applyPreset,
    });

    const randomizeParameters = useCallback(() => {
        const w = Math.max(120, Math.round(randI(400, 1200) / 40) * 40);
        const h = Math.max(120, Math.round(randI(320, 960) / 40) * 40);
        setWidth(w);
        setHeight(h);
        setMarginFactor(
            Math.round(randF(0.15, 0.75) * 100) / 100,
        );
        setCellSize(randI(5, 22));
        setSeed(
            `flow-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        );
        const modes: FlowAngleNoiseMode[] = [
            "perlin",
            "fbm",
            "domain_warp",
        ];
        setAngleNoiseMode(modes[randI(0, modes.length - 1)]!);
        setAngleNoiseOctaves(randI(2, 6));
        setAngleWarpStrength(Math.round(randF(0.15, 0.85) * 100) / 100);
        setAngleWarpFrequency(Math.round(randF(0.4, 2.2) * 100) / 100);
        setNoiseScale(Math.round(randF(0.04, 0.22) * 1000) / 1000);
        setStepNoiseScale(Math.round(randF(0.05, 0.24) * 1000) / 1000);
        setStepVariation(Math.round(randF(0, 1) * 100) / 100);
        const qOpts = [0, 0, 0, 5, 10, 15, 22.5, 30, 45];
        setAngleQuantizeDeg(qOpts[randI(0, qOpts.length - 1)] ?? 0);
        setFieldRotationDeg(Math.round(randF(-90, 90) * 2) / 2);
        setMirrorNoiseX(Math.random() < 0.2);
        setMirrorNoiseY(Math.random() < 0.2);
        setUniformFlowBlend(Math.round(randF(0, 0.45) * 100) / 100);
        setUniformFlowAngleDeg(Math.round(randF(0, 360) / 5) * 5);
        const shapeMode: FlowShapeMode = Math.random() < 0.72 ? "full" : "circle";
        setFlowShapeMode(shapeMode);
        setFlowShapeCircleRadiusScale(
            shapeMode === "circle"
                ? Math.round(randF(0.3, 1) * 100) / 100
                : 1,
        );
        setBidirectional(Math.random() < 0.35);
        setStepLength(Math.round(randF(1, 7.5) * 4) / 4);
        setMaxSteps(Math.round(randI(14, 55)) * 10);
        setStartMode(Math.random() < 0.55 ? "grid" : "random");
        setStartSpacing(randI(8, 32));
        setCurveCount(Math.round(randI(500, 3500) / 50) * 50);
        setStrokeWidth(Math.round(randF(0.2, 1.15) * 100) / 100);
        setStrokeColor(
            `#${randomHexByte(22, 92)}${randomHexByte(22, 92)}${randomHexByte(22, 92)}`,
        );
        setBackgroundColor(
            `#${randomHexByte(244, 255)}${randomHexByte(242, 255)}${randomHexByte(238, 255)}`,
        );
    }, []);

    const value = useMemo(
        (): FlowFieldState => ({
            width,
            height,
            marginFactor,
            cellSize,
            seed,
            angleNoiseMode,
            angleNoiseOctaves,
            angleWarpStrength,
            angleWarpFrequency,
            noiseScale,
            stepNoiseScale,
            stepVariation,
            angleQuantizeDeg,
            fieldRotationDeg,
            mirrorNoiseX,
            mirrorNoiseY,
            uniformFlowBlend,
            uniformFlowAngleDeg,
            flowShapeMode,
            flowShapeCircleRadiusScale,
            bidirectional,
            stepLength,
            maxSteps,
            startMode,
            startSpacing,
            curveCount,
            strokeColor,
            strokeWidth,
            backgroundColor,
            exportBackground,
            showAngleDebug,
            drawDots,
            dotMinGapExtra,
            dotMinGapAtLeastStroke,
            setWidth,
            setHeight,
            setMarginFactor,
            setCellSize,
            setSeed,
            setAngleNoiseMode,
            setAngleNoiseOctaves,
            setAngleWarpStrength,
            setAngleWarpFrequency,
            setNoiseScale,
            setStepNoiseScale,
            setStepVariation,
            setAngleQuantizeDeg,
            setFieldRotationDeg,
            setMirrorNoiseX,
            setMirrorNoiseY,
            setUniformFlowBlend,
            setUniformFlowAngleDeg,
            setFlowShapeMode,
            setFlowShapeCircleRadiusScale,
            setBidirectional,
            setStepLength,
            setMaxSteps,
            setStartMode,
            setStartSpacing,
            setCurveCount,
            setStrokeColor,
            setStrokeWidth,
            setBackgroundColor,
            setExportBackground,
            setShowAngleDebug,
            setDrawDots,
            setDotMinGapExtra,
            setDotMinGapAtLeastStroke,
            viewBoxAttr,
            pathDs,
            curvePoints,
            debugSegments,
            canExport,
            buildExportSvgString,
            handleDownload,
            randomizeParameters,
            applyPreset,
            getPresetValues,
            resetParameters,
        }),
        [
            width,
            height,
            marginFactor,
            cellSize,
            seed,
            angleNoiseMode,
            angleNoiseOctaves,
            angleWarpStrength,
            angleWarpFrequency,
            noiseScale,
            stepNoiseScale,
            stepVariation,
            angleQuantizeDeg,
            fieldRotationDeg,
            mirrorNoiseX,
            mirrorNoiseY,
            uniformFlowBlend,
            uniformFlowAngleDeg,
            flowShapeMode,
            flowShapeCircleRadiusScale,
            bidirectional,
            stepLength,
            maxSteps,
            startMode,
            startSpacing,
            curveCount,
            strokeColor,
            strokeWidth,
            backgroundColor,
            exportBackground,
            showAngleDebug,
            drawDots,
            dotMinGapExtra,
            dotMinGapAtLeastStroke,
            viewBoxAttr,
            pathDs,
            curvePoints,
            debugSegments,
            canExport,
            buildExportSvgString,
            handleDownload,
            randomizeParameters,
            applyPreset,
            getPresetValues,
            resetParameters,
        ],
    );

    return (
        <FlowFieldContext.Provider value={value}>
            {children}
        </FlowFieldContext.Provider>
    );
}
